import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import { Box, Button, ButtonBase, Divider, Skeleton, Stack, Typography } from '@mui/material';
import {
  BUSINESS_UI_STATUS,
  BUSINESS_UI_STATUS_LABEL,
  businessUiStatusColor,
} from '@/consts/businesses';
import { useBusiness } from '@/contexts/BusinessContext';
import Iconify from '@/components/iconify';
import Label from '@/components/label';
import MenuPopover from '@/components/menu-popover';
import { PATH_DASHBOARD } from '@/routes/paths';

const getInitials = (name) =>
  String(name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const BusinessAvatar = ({ business, size = 36 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: 1.25,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text.secondary',
      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
    }}
  >
    {business?.isCompany ? (
      <Iconify icon="solar:buildings-2-bold" width={size / 2} />
    ) : (
      <Typography variant="caption" sx={{ fontWeight: 'fontWeightBold' }}>
        {getInitials(business?.name)}
      </Typography>
    )}
  </Box>
);

BusinessAvatar.propTypes = {
  business: PropTypes.object,
  size: PropTypes.number,
};

// Negocio pessoal com status ambiguo (Em analise/Reprovado) nunca mostra selo:
// o status dele pode vir de um billing_status bloqueado (ui_status() conflacia
// BLOCKED em REJECTED), que nao e "negocio adicional reprovado" nenhum. Aprovado
// continua aparecendo normalmente — esse status nao e ambiguo.
const isAmbiguousPersonalStatus = (business) =>
  Boolean(business?.isPersonal) && business?.uiStatus !== BUSINESS_UI_STATUS.APPROVED;

// Mesmo criterio do travamento (src/utils/businessLock.js): o selo acompanha
// o status quando o negocio tem os dados preenchidos. Sem documento, nada de
// selo.
const hasStatusToShow = (business) =>
  !isAmbiguousPersonalStatus(business) &&
  Boolean(business?.uiStatus) &&
  Boolean(business?.hasDocument);

// Na lista o selo aparece em todos, inclusive Aprovado. No recolhido o protótipo
// usa duas marcas diferentes: selo quando o proprio negocio ativo exige atencao,
// e so uma bolinha quando quem exige atencao e outro negocio da conta.
const showsStatusOnTrigger = (business) =>
  hasStatusToShow(business) && business?.uiStatus !== BUSINESS_UI_STATUS.APPROVED;

const needsAttention = (business) =>
  hasStatusToShow(business) && business?.uiStatus !== BUSINESS_UI_STATUS.APPROVED;

const BusinessStatusLabel = ({ business }) => (
  <Label
    variant="soft"
    color={businessUiStatusColor(business)}
    sx={{ flexShrink: 0, height: 20, px: 0.75, fontSize: 11, textTransform: 'none' }}
  >
    {BUSINESS_UI_STATUS_LABEL[business?.uiStatus]}
  </Label>
);

BusinessStatusLabel.propTypes = {
  business: PropTypes.object,
};

const BusinessIdentity = ({ business, status }) => (
  <Box sx={{ minWidth: 0, flexGrow: 1, textAlign: 'left' }}>
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" noWrap>
        {business?.name}
      </Typography>

      {status}
    </Stack>

    <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
      {business?.documentLabel}
    </Typography>
  </Box>
);

BusinessIdentity.propTypes = {
  business: PropTypes.object,
  status: PropTypes.node,
};

export default function BusinessSwitcher() {
  const {
    businesses,
    activeBusiness,
    isLoading,
    isError,
    isImpersonating,
    refetchBusinesses,
    switchBusiness,
  } = useBusiness();

  const [openPopover, setOpenPopover] = useState(null);

  const navigate = useNavigate();

  const handleOpenPopover = (event) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleSelectBusiness = (businessId) => {
    handleClosePopover();
    switchBusiness(businessId);
  };

  const handleCreateBusiness = () => {
    handleClosePopover();
    navigate(PATH_DASHBOARD.business.new);
  };

  if (isLoading) {
    return (
      <Stack spacing={0.75}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Negócio
        </Typography>
        <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2 }} />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack
        spacing={1}
        sx={{ p: 2, borderRadius: 2, border: (theme) => `solid 1px ${theme.palette.divider}` }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Não foi possível carregar seus negócios.
        </Typography>

        <Button size="small" variant="outlined" color="inherit" onClick={() => refetchBusinesses()}>
          Tentar novamente
        </Button>
      </Stack>
    );
  }

  if (!activeBusiness) {
    return null;
  }

  const showsAttentionDot =
    !showsStatusOnTrigger(activeBusiness) &&
    businesses.some(
      (business) => String(business.id) !== String(activeBusiness.id) && needsAttention(business)
    );

  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Negócio
      </Typography>

      <ButtonBase
        // Sob impersonação o backend devolve só o negócio padrão e ignora o header,
        // então a troca não teria efeito: o seletor fica travado nele (CBK-571).
        disabled={isImpersonating}
        aria-haspopup="listbox"
        aria-expanded={Boolean(openPopover)}
        aria-label={
          isImpersonating
            ? `Negócio ativo: ${activeBusiness.name}`
            : `Negócio ativo: ${activeBusiness.name}. Trocar de negócio`
        }
        onClick={handleOpenPopover}
        sx={{
          p: 1.25,
          width: 1,
          gap: 1.25,
          borderRadius: 1.5,
          justifyContent: 'flex-start',
          border: (theme) => `solid 1px ${theme.palette.divider}`,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <BusinessAvatar business={activeBusiness} />

        <BusinessIdentity
          business={activeBusiness}
          status={
            showsStatusOnTrigger(activeBusiness) ? (
              <BusinessStatusLabel business={activeBusiness} />
            ) : null
          }
        />

        {showsAttentionDot && (
          <Box
            aria-label="Há negócio aguardando atenção"
            sx={{ width: 6, height: 6, flexShrink: 0, borderRadius: '50%', bgcolor: 'warning.main' }}
          />
        )}

        <Iconify
          icon={
            (isImpersonating && 'eva:lock-fill') ||
            (openPopover ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill')
          }
          width={18}
          sx={{ flexShrink: 0, color: 'text.secondary' }}
        />
      </ButtonBase>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        disabledArrow
        sx={{ p: 1, width: 320 }}
      >
        <Typography
          variant="caption"
          sx={{ px: 1, py: 0.5, display: 'block', color: 'text.secondary' }}
        >
          Seus negócios
        </Typography>

        <Stack sx={{ mt: 0.5 }}>
          {businesses.map((business) => {
            const isActive = String(business.id) === String(activeBusiness.id);

            return (
              <ButtonBase
                key={business.id}
                aria-current={isActive}
                onClick={() => handleSelectBusiness(business.id)}
                sx={{
                  p: 1,
                  gap: 1.25,
                  borderRadius: 1.25,
                  justifyContent: 'flex-start',
                  ...(isActive && { bgcolor: 'action.selected' }),
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <BusinessAvatar business={business} size={32} />

                <BusinessIdentity business={business} />

                {hasStatusToShow(business) && <BusinessStatusLabel business={business} />}
              </ButtonBase>
            );
          })}
        </Stack>

        <Divider sx={{ my: 1 }} />

        <ButtonBase
          onClick={handleCreateBusiness}
          sx={{
            p: 1,
            gap: 1.25,
            width: 1,
            borderRadius: 1.25,
            justifyContent: 'flex-start',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              display: 'flex',
              borderRadius: 1.5,
              flexShrink: 0,
              alignItems: 'center',
              color: 'primary.main',
              justifyContent: 'center',
              border: (theme) => `dashed 1px ${alpha(theme.palette.primary.main, 0.48)}`,
            }}
          >
            <Iconify icon="eva:plus-fill" width={18} />
          </Box>

          <Box sx={{ minWidth: 0, textAlign: 'left' }}>
            <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
              Novo Negócio
            </Typography>
          </Box>
        </ButtonBase>
      </MenuPopover>
    </Stack>
  );
}
